import { Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import { authBackoff } from '../utils/exponential-backoff';
import { logger } from '../utils/logger';

/**
 * Token Manager
 * Handles token refresh with proper error handling and backoff
 */
export class TokenManager {
  private refreshQueue: Promise<Session> | null = null;
  private lastRefreshError: AuthError | null = null;
  private failedRefreshCount = 0;
  
  /**
   * Refresh the current session token with retry logic
   */
  async refreshToken(): Promise<Session> {
    // If a refresh is already in progress, wait for it
    if (this.refreshQueue) {
      logger.debug('Token refresh already in progress, joining queue');
      return this.refreshQueue;
    }
    
    // Create a new refresh promise
    this.refreshQueue = this.performRefresh()
      .finally(() => {
        this.refreshQueue = null;
      });
    
    return this.refreshQueue;
  }
  
  private async performRefresh(): Promise<Session> {
    try {
      logger.debug('Starting token refresh with exponential backoff');
      
      const session = await authBackoff.execute(
        async () => {
          const { data, error } = await supabase.auth.refreshSession();
          
          if (error) {
            // Track specific error types
            if (this.isInvalidTokenError(error)) {
              logger.error('Invalid refresh token detected', { error });
              throw new InvalidTokenError(error.message);
            }
            
            throw error;
          }
          
          if (!data.session) {
            throw new Error('No session returned from refresh');
          }
          
          return data.session;
        },
        (error) => {
          // Don't retry invalid token errors
          if (error instanceof InvalidTokenError) {
            return false;
          }
          
          // Don't retry after too many failures
          if (this.failedRefreshCount >= 3) {
            logger.warn('Too many refresh failures, stopping retries');
            return false;
          }
          
          // Retry network and server errors
          const shouldRetry = this.isRetriableError(error);
          if (shouldRetry) {
            this.failedRefreshCount++;
            logger.debug('Retrying token refresh', { 
              attempt: this.failedRefreshCount,
              error: error.message 
            });
          }
          
          return shouldRetry;
        }
      );
      
      // Reset failure count on success
      this.failedRefreshCount = 0;
      this.lastRefreshError = null;
      logger.info('Token refresh successful');
      
      return session;
      
    } catch (error) {
      logger.error('Token refresh failed after retries', { error });
      this.lastRefreshError = error as AuthError;
      
      // If it's an invalid token error, we need to sign out
      if (error instanceof InvalidTokenError) {
        logger.warn('Invalid token detected, clearing session');
        await this.clearInvalidSession();
      }
      
      throw error;
    }
  }
  
  private isInvalidTokenError(error: AuthError): boolean {
    return (
      error.message?.includes('refresh_token_not_found') ||
      error.message?.includes('invalid refresh token') ||
      error.message?.includes('Token has expired') ||
      error.status === 400 ||
      error.status === 401
    );
  }
  
  private isRetriableError(error: any): boolean {
    // Network errors
    if (error.name === 'NetworkError' || error.message?.includes('network')) {
      return true;
    }
    
    // Server errors (5xx)
    if (error.status >= 500 && error.status < 600) {
      return true;
    }
    
    // Rate limiting (429)
    if (error.status === 429) {
      return true;
    }
    
    // Timeout errors
    if (error.message?.includes('timeout')) {
      return true;
    }
    
    return false;
  }
  
  private async clearInvalidSession(): Promise<void> {
    try {
      // Sign out to clear bad tokens
      await supabase.auth.signOut();
      
      // Clear any stored session data
      if (typeof window !== 'undefined') {
        localStorage.removeItem('supabase.auth.token');
        sessionStorage.removeItem('supabase.auth.token');
      }
    } catch (error) {
      logger.error('Failed to clear invalid session', { error });
    }
  }
  
  /**
   * Get the last refresh error if any
   */
  getLastError(): AuthError | null {
    return this.lastRefreshError;
  }
  
  /**
   * Reset the token manager state
   */
  reset(): void {
    this.refreshQueue = null;
    this.lastRefreshError = null;
    this.failedRefreshCount = 0;
  }
}

/**
 * Custom error class for invalid token errors
 */
class InvalidTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidTokenError';
  }
}

// Singleton instance
export const tokenManager = new TokenManager();